import React, { ReactElement, useEffect, useState } from 'react';
import { ElearningReviewersStateType } from '@/modules/types/elearningReviewers';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Select, Modal, Alert, Spinner } from 'elmo-elements';

import { actions as ElearningReviewersActions } from '@/modules/reducers/elearningReviewers';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import FormItemWrapper from '@/components/Form/FormItemWrapper';
import {
  useGetTmsReviewersQuery,
  useCreateReviewerMutation,
  useGetElearningQuery,
} from '@/modules/services';
import { useParams } from 'react-router-dom';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { ElearningParamType } from '@/pages/Module/routes';
import { appData } from '@/lib/data';
import CKEditorWrapper from '@/components/CKEditorWrapper';
import { getCkEditorConfig } from '@/lib/util';
import './ReviewerModal.scss';
import { ReviewerStatus } from '@/modules/types/reviewers';

interface UserType {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface UserOptionType {
  value: string;
  label: string;
  reviewer: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface UserSearchQueryParam {
  page: number;
  searchQueryText: string;
}

const defaultValues = {
  status: ReviewerStatus.NOT_STARTED,
};

const ReviewerModal = (): ReactElement => {
  const [selectedUser, setSelectedUser] = useState<null | UserOptionType>(null);
  const { elearningId } = useParams<ElearningParamType>();
  const [reviewerList, setReviewerList] = useState<[] | UserOptionType[]>([]);
  const { data: elearningData } = useGetElearningQuery(elearningId);
  const defaultSearchQueryParam: UserSearchQueryParam = {
    page: 1,
    searchQueryText: '',
  };
  const [searchQueryParam, setSearchQueryParam] =
    useState<UserSearchQueryParam>(defaultSearchQueryParam);

  const dispatch = useDispatch();

  const elearningReviewersState: ElearningReviewersStateType = useSelector(
    (state: { elearningReviewers: ElearningReviewersStateType }) =>
      state.elearningReviewers,
  );

  const getTmsReviewers = useGetTmsReviewersQuery(searchQueryParam, {
    skip: searchQueryParam.searchQueryText.length < 3,
  });

  useEffect(() => {
    if (!getTmsReviewers.data) return;

    const searchResult =
      getTmsReviewers.data?.data?.map((user: UserType) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName} (${user.email})`,
        reviewer: {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        },
      })) ?? [];

    setReviewerList([...searchResult, ...reviewerList]);
  }, [getTmsReviewers.data]);

  const generateMessageTemplate = (): string =>
    appData.elearnings.review.message.replace(
      '[[FIRSTNAME]]',
      selectedUser?.reviewer.firstName ? selectedUser.reviewer.firstName : '',
    );

  const [createReviewer, createReviewerStatus] = useCreateReviewerMutation();

  interface EditReviewerFormType {
    tmsReviewerUserId: string;
    message: string;
    status: number;
  }

  type FieldNameType = 'tmsReviewerUserId' | 'message';

  const schema = yup.object().shape({
    tmsReviewerUserId: yup.string().required().label('User'),
    message: yup.string().required().label('Message'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<EditReviewerFormType>({
    resolver: yupResolver(schema),
    defaultValues: {
      tmsReviewerUserId: '',
      message: generateMessageTemplate(),
      status: defaultValues.status,
    },
  });

  const closeReviewerModal = () => {
    setSearchQueryParam(defaultSearchQueryParam);
    setReviewerList([]);
    setSelectedUser(null);
    reset();
    dispatch({
      type: ElearningReviewersActions.closeReviewerModal.type,
    });
  };

  const onSubmit = async (data: EditReviewerFormType) => {
    const result = await createReviewer({
      elearningId,
      ...data,
    });

    // @todo: error does not exist in result
    if (!(result as any).error) {
      closeReviewerModal();
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Reviewer added',
        },
      });
    }
  };

  const getErrorMessage = () => {
    const error = createReviewerStatus.error as any;
    return error.data && error.data.message;
  };

  const handleFieldChange = (e: any, fieldName: FieldNameType) => {
    const value = e.target ? e.target.value : e;
    setValue(fieldName, value);
  };

  return (
    <Modal
      id="ReviewerModal"
      isOpened={elearningReviewersState.isReviewerModalOpen}
      closeModal={closeReviewerModal}
      title="Add Reviewer"
    >
      {createReviewerStatus.isError && (
        <Alert type="danger" message={getErrorMessage()} isCloseable={false} />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="reviewer_spinner_wrapper">
          <FormItemWrapper
            label="Search Reviewers"
            yupFieldError={errors.tmsReviewerUserId}
            className="form-item--search"
          >
            <Select
              placeholder="Start typing first name"
              onInputChange={(keyword: string) => {
                setReviewerList([]);
                setSearchQueryParam({ page: 1, searchQueryText: keyword });
              }}
              closeMenuOnSelect={true}
              onBlur={() => {
                setSearchQueryParam(defaultSearchQueryParam);
                setReviewerList([]);
              }}
              onMenuScrollToBottom={() => {
                if (
                  getTmsReviewers.data?.data &&
                  getTmsReviewers.data.data.length > 0 &&
                  searchQueryParam.page < 3 // setting it to 3 because we are loading 100 items per page and scrolling more than 2 page to find user is not ideal
                ) {
                  setSearchQueryParam({
                    page: searchQueryParam.page + 1,
                    searchQueryText: searchQueryParam.searchQueryText,
                  });
                }
              }}
              onChange={(option: UserOptionType) => {
                setSelectedUser(option);
                setTimeout(() => {
                  document.getElementById('EditReviewerFormMessage')?.focus();
                }, 10);
                setValue('tmsReviewerUserId', option.value);

                (
                  document.getElementById(
                    'tmsReviewerUserIdHidden',
                  ) as HTMLFormElement
                ).value = option.value;
              }}
              options={reviewerList}
            />
            <input
              {...register('tmsReviewerUserId', {})}
              id="tmsReviewerUserIdHidden"
              type="hidden"
            />
          </FormItemWrapper>
          {getTmsReviewers.status === 'pending' && (
            <Spinner id="ReviewerSearchSpinner" size="s" />
          )}
        </div>
        <FormItemWrapper label="Message" yupFieldError={errors.message}>
          <CKEditorWrapper
            {...register('message', {})}
            id="EditReviewerFormMessage"
            name="message"
            value={watch('message')}
            initData={generateMessageTemplate()}
            setValue={setValue}
            onChange={(e) => handleFieldChange(e, 'message')}
            toolbar="basic"
            readOnly={!selectedUser}
            key={`${selectedUser?.value}`}
            config={getCkEditorConfig({
              tenantId: `${elearningData?.tenantId}`,
              type: 'elearnings',
              elearningId,
            })}
          />
        </FormItemWrapper>
        <div className="float-right pb-4">
          <Button
            id="EditReviewerFormSend"
            type="primary"
            htmlType="submit"
            size="xl"
            isLoading={createReviewerStatus.isLoading}
            className="px-4"
          >
            Send
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ReviewerModal;
